/**
 * Environment utilities for mobile app
 */

export type Environment = 'development' | 'staging' | 'production';
export type ApnsEnvironment = 'development' | 'production';

export function getEnvironment(): Environment {
  const env =
    typeof import.meta.env.VITE_APP_ENV === 'string' ? import.meta.env.VITE_APP_ENV : 'development';
  if (env === 'development' || env === 'staging' || env === 'production') {
    return env;
  }
  throw new Error('Invalid VITE_APP_ENV');
}

export function getSupabaseUrl(): string {
  return import.meta.env.VITE_SUPABASE_URL;
}

export function getSupabaseAnonKey(): string {
  return import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
}

/**
 * Returns a badge string for non-production environments
 * Useful for displaying environment indicators in the UI
 */
export function getEnvironmentBadge(): string | null {
  const env = getEnvironment();
  if (env === 'production') {
    return null;
  }
  return env.toUpperCase();
}

/**
 * Get the APNs entitlement environment embedded into the current Vite build.
 */
export function getApnsEnvironment(): ApnsEnvironment {
  const env =
    typeof import.meta.env.VITE_APNS_ENV === 'string' ? import.meta.env.VITE_APNS_ENV : undefined;
  if (env === 'development' || env === 'production') {
    return env;
  }
  throw new Error('Invalid VITE_APNS_ENV');
}

/**
 * Check if current environment is production
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * Check if current environment is development
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Get the bundle ID for the current environment
 * Used for OAuth redirect URLs on native platforms
 */
export function getBundleId(): string {
  const env = getEnvironment();
  switch (env) {
    case 'development':
      return 'com.publicpulse.app.dev';
    case 'staging':
      return 'com.publicpulse.app.staging';
    case 'production':
      return 'com.publicpulse.app';
    default:
      return 'com.publicpulse.app.dev';
  }
}

/**
 * Universal Link callback registered with Supabase OAuth providers.
 */
export function getCallbackUrl(): string {
  const env = getEnvironment();
  switch (env) {
    case 'production':
      return 'https://app.publicpulse.com/auth/callback';
    case 'staging':
      return 'https://stg.publicpulse.com/auth/callback';
    case 'development':
      return 'https://dev.publicpulse.com/auth/callback';
    default:
      return 'https://dev.publicpulse.com/auth/callback';
  }
}

/**
 * Native app callback URL registered in iOS Info.plist CFBundleURLTypes.
 */
export function getNativeAuthCallbackUrl(): string {
  return `${getBundleId()}://auth/callback`;
}

/**
 * Web-side auth redirect URL (current origin's `/auth/callback`).
 */
export function getWebAuthRedirectUrl(): string {
  return `${globalThis.location.origin}/auth/callback`;
}
